import React, { ChangeEventHandler, forwardRef, useState } from 'react';
import { isNil } from 'lodash';
import classnames from 'classnames';
import { Box, utilityClasses } from '@fiverr-private/theme';
import { getBorderColor } from '../shared/getTextInputsStyle';
import { TextareaProps } from './types';
import { resizeClass, textarea } from './styles.ve.css';
import { getTextareaStylingProps } from './style';
import { TEXTAREA_ID } from './constants';

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      children,
      disabled,
      isSuccess = false,
      isError = false,
      maxLength,
      value,
      defaultValue,
      onChange = Function.prototype,
      resize = false,
      className,
      width = '100%',
      dataTrackTag = 'textarea',
      dataTrackValue = '<value>',
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState(defaultValue || children);
    const currentValue = isNil(value) ? innerValue : value;

    const props: TextareaProps = {
      disabled,
      maxLength,
      value: currentValue,
      ...rest,
    };

    const classNames = classnames(textarea, utilityClasses.placeholder, className, {
      [resizeClass]: resize,
    });

    const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      setInnerValue(e.target.value);
      onChange(e);
    };

    const borderColor = getBorderColor({ disabled, isSuccess, isError });

    return (
      <Box
        as="textarea"
        boxSizing="borderBox"
        paddingY="2"
        paddingX="4"
        {...getTextareaStylingProps({
          borderColor,
          disabled,
          focusBorderColor: borderColor.focus,
          focusType: 'focus',
        })}
        {...props}
        width={width}
        role="textbox"
        className={classNames}
        ref={ref}
        onChange={handleChange}
        pointerEvents={disabled ? 'none' : undefined}
        dataTrackTag={dataTrackTag}
        dataTrackValue={dataTrackValue}
      />
    );
  }
);

Textarea.displayName = TEXTAREA_ID;
Object.assign(Textarea, {
  id: TEXTAREA_ID,
});
export default Textarea;
